import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PERMISSION_TABS = [
  { id: "dashboard", label: "Tableau de bord" },
  { id: "reservations", label: "Réservations" },
  { id: "clients", label: "Clients" },
  { id: "commandes", label: "Commandes" },
  { id: "boutique", label: "Boutique" },
  { id: "promotions", label: "Promotions" },
  { id: "faq", label: "FAQ" },
  { id: "sections", label: "Sections" },
  { id: "editeur", label: "Éditeur visuel" },
  { id: "themes", label: "Thèmes" },
  { id: "styles", label: "Styles" },
  { id: "agents", label: "Agents IA" },
  { id: "marketing", label: "Marketing" },
  { id: "notifications", label: "Notifications" },
  { id: "medias", label: "Médias" },
  { id: "sauvegardes", label: "Sauvegardes" },
  { id: "seo", label: "SEO" },
  { id: "services", label: "Services" },
  { id: "cartes-cadeaux", label: "Cartes cadeaux" },
  { id: "carte", label: "Carte" },
  { id: "evenements", label: "Événements" },
  { id: "galerie", label: "Galerie" },
  { id: "avis", label: "Avis" },
  { id: "navigation", label: "Navigation" },
  { id: "messages", label: "Messages" },
  { id: "design", label: "Design" },
  { id: "reglages", label: "Réglages" },
];

const emptyUser = { email: "", password: "", name: "", role: "editeur", permissions: [] };

export default function TeamManager() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/users/${editing.id}`, editing);
        toast.success("Compte mis à jour.");
      } else {
        await api.post("/admin/users", editing);
        toast.success("Compte créé.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Compte supprimé.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePerm = (perm) =>
    setEditing((u) => ({
      ...u,
      permissions: u.permissions.includes(perm)
        ? u.permissions.filter((p) => p !== perm)
        : [...u.permissions, perm],
    }));

  return (
    <div data-testid="team-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Équipe & accès</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ivory/40">
            Créez des comptes pour votre équipe. Le rôle « Éditeur » n'accède qu'aux onglets cochés ;
            le rôle « Admin » a accès à tout, y compris à cette page.
          </p>
        </div>
        <button onClick={() => setEditing({ ...emptyUser })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="user-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter un compte
        </button>
      </div>

      <div className="space-y-2" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`user-row-${u.id}`}>
            <span className="text-sm text-ivory">{u.name || u.email}</span>
            <span className="text-xs text-ivory/45">{u.email}</span>
            <span className={`flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] font-semibold ${u.role === "admin" ? "border-gold/40 text-gold" : "border-white/15 text-ivory/60"}`}>
              {u.role === "admin" && <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />}
              {u.role === "admin" ? "Admin" : `Éditeur · ${(u.permissions || []).length} onglet(s)`}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setEditing({ ...emptyUser, ...u, password: "" })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`user-edit-${u.id}`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(u.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`user-delete-${u.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="user-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">{editing.id ? "Modifier le compte" : "Nouveau compte"}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required type="email" value={editing.email} onChange={(e) => setEditing((u) => ({ ...u, email: e.target.value }))} placeholder="Email" disabled={!!editing.id} className="input-dark !h-11 disabled:opacity-50" data-testid="user-email-input" />
            <input value={editing.name} onChange={(e) => setEditing((u) => ({ ...u, name: e.target.value }))} placeholder="Nom (ex : Marie — salle)" className="input-dark !h-11" data-testid="user-name-input" />
            <input type="password" value={editing.password} onChange={(e) => setEditing((u) => ({ ...u, password: e.target.value }))} placeholder={editing.id ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe (8 caractères min.)"} className="input-dark !h-11" data-testid="user-password-input" />
            <div className="flex gap-2">
              {[
                { v: "editeur", l: "Éditeur" },
                { v: "admin", l: "Admin (tous les droits)" },
              ].map((r) => (
                <button
                  type="button"
                  key={r.v}
                  onClick={() => setEditing((u) => ({ ...u, role: r.v }))}
                  className={`rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${editing.role === r.v ? "border-copper bg-copper text-ivory" : "border-white/10 text-ivory/60 hover:border-copper/50"}`}
                  data-testid={`user-role-${r.v}`}
                >
                  {r.l}
                </button>
              ))}
            </div>
            {editing.role === "editeur" && (
              <div className="grid grid-cols-2 gap-2 rounded-md border border-white/10 p-4 sm:grid-cols-3" data-testid="user-permissions">
                {PERMISSION_TABS.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs text-ivory/70">
                    <input
                      type="checkbox"
                      checked={editing.permissions.includes(t.id)}
                      onChange={() => togglePerm(t.id)}
                      className="accent-[#C4704F]"
                      data-testid={`user-perm-${t.id}`}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            )}
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="user-save-button">
              <UserPlus className="h-4 w-4" strokeWidth={1.5} />
              {saving ? "Enregistrement…" : editing.id ? "Mettre à jour" : "Créer le compte"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
